import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { OrderCreatedEvent } from '../../events.js';
import { createOrder } from '../../order-api.js';
import { writeLocalOrderStorage } from '../../order-storage.js';
import newOrderStyles from './new-order.styles.js';


@customElement('new-order')
export class NewOrder extends LitElement {
  static styles = newOrderStyles;

  @state() private orderCode: string | null = null;
  @state() private isLoading = false;
  @state() private announcement = '';

  private linkRef = createRef<HTMLAnchorElement>();

  createRenderRoot() {
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.adoptedStyleSheets = [];
    }

    return super.createRenderRoot();
  }

  private async createOrder() {
    this.isLoading = true;
    this.orderCode = null;
    this.announcement = 'Auftrag wird erstellt...';

    try {
      const { code } = await createOrder();
      writeLocalOrderStorage(code);
      window.dispatchEvent(new OrderCreatedEvent(code));

      this.announcement = '';
      this.orderCode = code;

      await this.updateComplete;

      this.linkRef.value?.focus();
    } catch (error) {
      this.announcement = 'Fehler beim Erstellen des Auftrags.';
    } finally {
      this.isLoading = false;
    }
  }

  private renderStatusContent() {
    if (!this.orderCode) return null;

    return html`
      <p aria-hidden="true">Auftrag erfolgreich erstellt!</p>
      <a
        ${ref(this.linkRef)}
        href="/order/status/?code=${encodeURIComponent(this.orderCode)}"
        aria-label="Auftrag erfolgreich erstellt. Details zum Auftrag ansehen."
      >
        Details zum Auftrag ${this.orderCode} ansehen
      </a>
    `;
  }

  render() {
    return html`
      <div>
        <button @click=${this.createOrder} ?disabled=${this.isLoading}>
          <span>Auftrag anlegen</span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="currentColor"
          >
            <path
              d="m176-120-56-56 301-302-181-45 198-123-17-234 179 151 216-88-87 217 151 178-234-16-124 198-45-181-301 301Zm24-520-80-80 80-80 80 80-80 80Zm355 197 48-79 93 7-60-71 35-86-86 35-71-59 7 92-79 49 90 22 23 90Zm165 323-80-80 80-80 80 80-80 80ZM569-570Z"
            />
          </svg>
        </button>

        <div id="order-info">${this.renderStatusContent()}</div>

        <div class="visually-hidden" aria-live="polite">
          ${this.announcement}
        </div>
      </div>
    `;
  }
}
